import type { AppViewsType } from "@/state/@types";

// =============================================================================
// Node-panel eligibility, per app view
// =============================================================================
// The Node panel is *contextual*: it only makes sense in views that have a
// concept of a "selected node" (e.g. workflow steps, transactions, accounts).
// Views like ``onboarding`` don't have such a concept — they show no Node
// panel at all. ``triage`` and ``thread`` support drilling into entities (and
// related objects) from overlines / referenced nodes — triage via the shared
// Node panel, thread via ``ThreadPanelLayout`` on the right.
//
// Each entry declares ``allowedTypenames`` — the ``__typename``s (or
// ``__typename`` *prefixes*; see ``isTypenameValidForView``) that are valid
// as the panel's ``activePanelNodeId`` in that view. ``NodePanelLayout``
// falls back to an empty state when the resolved typename doesn't match
// (so a stale id from a previous view never renders against the wrong
// adapter).
//
// Views absent from the map have *no* Node panel; ``AppTopbarPanelToggle``
// hides the ``Node`` / ``Both`` segments in those views.
//
// ``ThreadObject`` is valid in every view that has a panel: the thread panel
// is global, and the changes a thread made open beside whatever is on screen.
// =============================================================================

export type NodePanelConfig = {
  /**
   * Either an exact ``__typename`` or a typename *prefix* (e.g.
   * ``"Rule"`` matches ``RuleDefinitionObject``, ``RuleEdgeObject`` etc.).
   * The prefix form keeps the config short while the registry grows
   * with new subtypes.
   */
  allowedTypenames: ReadonlyArray<string>;
  /**
   * The ``__typename`` a *routed detail page* shows for this view — the
   * record behind ``/app/documents/all/<identifier>``. Only the record's
   * ``identifier`` travels in the URL; the typename comes from here, so a
   * view can only route to one kind of detail record.
   *
   * Views without one have no detail route: their "detail" is either the
   * node panel alone or a bespoke canvas (rules, reports, threads).
   */
  detailTypename?: string;
};

export const NODE_PANEL_CONFIG: Partial<Record<AppViewsType, NodePanelConfig>> =
  {
    budget: {
      allowedTypenames: ["BudgetLineObject", "ThreadObject"],
    },
    rules: {
      allowedTypenames: [
        "RuleDefinitionObject",
        "RuleEdgeObject",
        "RuleDelegationObject",
        "ThreadObject",
      ],
    },
    transactions: {
      allowedTypenames: [
        "TransactionObject",
        "JournalEntryObject",
        "ChartedAccountObject",
        "ThreadObject",
      ],
      detailTypename: "TransactionObject",
    },
    "journal-entries": {
      allowedTypenames: [
        "JournalEntryObject",
        "TransactionObject",
        "ThreadObject",
      ],
      detailTypename: "JournalEntryObject",
    },
    "tax-codes": {
      allowedTypenames: ["TaxObject", "ThreadObject"],
      detailTypename: "TaxObject",
    },
    "financial-accounts": {
      // An account's page lists its transactions, and each opens beside it.
      allowedTypenames: [
        "FinancialAccountObject",
        "TransactionObject",
        "ThreadObject",
      ],
      detailTypename: "FinancialAccountObject",
    },
    "chart-of-accounts": {
      allowedTypenames: ["ChartedAccountObject", "ThreadObject"],
    },
    contacts: {
      allowedTypenames: ["ContactObject", "ThreadObject"],
      detailTypename: "ContactObject",
    },
    organizations: {
      allowedTypenames: ["OrganizationObject", "ContactObject", "ThreadObject"],
      detailTypename: "OrganizationObject",
    },
    documents: {
      allowedTypenames: ["DocumentObject", "ThreadObject"],
      detailTypename: "DocumentObject",
    },
    reports: {
      // Widget rows link the records they aggregate into the panel — a bank
      // accounts widget opens the entity or the account behind a row.
      allowedTypenames: [
        "ReportObject",
        "EntityObject",
        "FinancialAccountObject",
        "FinancialInstitutionObject",
        "ThreadObject",
      ],
    },
    triage: {
      allowedTypenames: [
        "DocumentObject",
        "FinancialAccountObject",
        "EntityObject",
        "TransactionObject",
        "RuleDefinitionObject",
        "ThreadObject",
      ],
    },
    // Thread list entity overlines open ``ThreadPanelLayout`` (not the
    // shared Node panel split — see ``isFullWidthView`` in ``page.tsx``).
    thread: {
      allowedTypenames: ["EntityObject", "ThreadObject"],
    },
  };

export function viewSupportsNodePanel(view: AppViewsType): boolean {
  return NODE_PANEL_CONFIG[view] != null;
}

/** ``__typename`` behind this view's routed detail page, if it has one. */
export function detailTypenameForView(view: AppViewsType): string | null {
  return NODE_PANEL_CONFIG[view]?.detailTypename ?? null;
}

/**
 * The view that owns the detail page for a ``__typename`` — the inverse of
 * ``detailTypenameForView``, used to turn a reference to a record into a link
 * to its page. ``null`` for typenames with no page of their own.
 */
export function detailViewForTypename(
  typename: string | null | undefined,
): AppViewsType | null {
  if (!typename) return null;
  for (const [view, config] of Object.entries(NODE_PANEL_CONFIG)) {
    if (config?.detailTypename === typename) {
      return view as AppViewsType;
    }
  }
  return null;
}

export function isTypenameValidForView(
  view: AppViewsType,
  typename: string | null | undefined,
): boolean {
  const cfg = NODE_PANEL_CONFIG[view];
  if (!cfg || !typename) return false;
  return cfg.allowedTypenames.some(
    (allowed) => typename === allowed || typename.startsWith(allowed),
  );
}

/**
 * Some views open a *detail* record (breadcrumb / ``activeTabNodeIds``) and
 * let the node panel drill into *child* objects (``activePanelNodeId``).
 * Rules are the canonical example: the detail is the ``RuleDefinitionObject``
 * while a selected step loads a ``RuleNode*`` in the panel. When the
 * two ids diverge, breadcrumbs and inline title edits must track the
 * detail record, not the panel selection.
 */
export function panelOverridesBreadcrumbDetail(
  detailNodeId: string | null | undefined,
  activePanelNodeId: string | null | undefined,
): boolean {
  return (
    detailNodeId != null &&
    activePanelNodeId != null &&
    activePanelNodeId !== detailNodeId
  );
}
